import React, { useState, useEffect, useRef } from 'react';
import { SVG } from '@svgdotjs/svg.js';

const Columnas = () => {
  const canvasRef = useRef(null);
  const interactionDiagramRef = useRef(null);
  const [b, setB] = useState(0.3); // Ancho de la columna en metros
  const [h, setH] = useState(0.3); // Altura de la columna en metros
  const [zoom, setZoom] = useState(1);
  const [recubrimiento, setRecubrimiento] = useState(0.04); // Recubrimiento en metros
  const [fc, setFc] = useState(21); // Resistencia del hormigón en MPa
  const [fy, setFy] = useState(420); // Resistencia del acero en MPa
  const [numBarras, setNumBarras] = useState(4); // Número de barras
  const [diametroBarras, setDiametroBarras] = useState(16); // Diámetro de barras en mm
  const [Pu, setPu] = useState(500); // Carga axial en kN
  const [Mu, setMu] = useState(50); // Momento flector en kN-m
  const [diagramPoints, setDiagramPoints] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);

  const barOptions = [12, 14, 16, 18, 20, 22, 25, 28, 32];
  const canvasSize = 800;

  // Dibujar la columna
  useEffect(() =>
  {
    if (!canvasRef.current) return;
    
    canvasRef.current.innerHTML = '';
    const draw = SVG().addTo(canvasRef.current).size('100%', '100%').viewbox(0, 0, canvasSize, canvasSize);
    
    const scale = canvasSize * zoom;
    const columnWidth = b * scale;
    const columnHeight = h * scale;
    const centerX = (canvasSize - columnWidth) / 2;
    const centerY = (canvasSize - columnHeight) / 2;

    // Dibujar la columna
    draw.rect(columnWidth, columnHeight)
      .fill('#e2e8f0')
      .stroke({ color: '#2d3748', width: 2 })
      .move(centerX, centerY);

    // Dibujar las barras de refuerzo
    const barDiameterPx = diametroBarras * zoom;
    const recubrimientoPx = recubrimiento * scale;
    
    // Coordenadas de las barras
    const barPositions = [];
    const spacingX = (columnWidth - 2 * recubrimientoPx - barDiameterPx) / (Math.sqrt(numBarras) - 1);
    const spacingY = (columnHeight - 2 * recubrimientoPx - barDiameterPx) / (Math.sqrt(numBarras) - 1);
    
    for (let i = 0; i < Math.sqrt(numBarras); i++) {
      for (let j = 0; j < Math.sqrt(numBarras); j++) {
        const x = centerX + recubrimientoPx + i * spacingX;
        const y = centerY + recubrimientoPx + j * spacingY;
        
        draw.circle(barDiameterPx)
          .fill('#2d3748')
          .move(x, y);
          
        barPositions.push({ x: x + barDiameterPx/2, y: y + barDiameterPx/2 });
      }
    }

    // Dibujar ejes de la columna
    draw.line(centerX + columnWidth/2, centerY - 20, centerX + columnWidth/2, centerY + columnHeight + 20)
      .stroke({ color: '#4a5568', width: 1, dasharray: '5,5' });
      
    draw.line(centerX - 20, centerY + columnHeight/2, centerX + columnWidth + 20, centerY + columnHeight/2)
      .stroke({ color: '#4a5568', width: 1, dasharray: '5,5' });

  }, [b, h, zoom, recubrimiento, numBarras, diametroBarras]);

  // Calcular diagrama de interacción
  useEffect(() => {
    if (!interactionDiagramRef.current) return;
    
    interactionDiagramRef.current.innerHTML = '';
    const draw = SVG().addTo(interactionDiagramRef.current).size('100%', '100%').viewbox(0, 0, 800, 500);
    
    // Parámetros del material
    const beta1 = fc <= 28 ? 0.85 : Math.max(0.65, 0.85 - 0.05 * (fc - 28) / 7);
    const ecu = 0.003;
    const ey = fy / 200000; // Deformación de fluencia
    
    // Geometría
    const As = (Math.PI * Math.pow(diametroBarras / 2 / 10, 2) * numBarras).toFixed(2); // Área de acero en cm²
    const d = h - recubrimiento;
    const dPrime = recubrimiento;
    
    // Puntos clave del diagrama
    const points = [];
    
    // 1. Punto de compresión pura (Pn max)
    const PnMax = 0.8 * (0.85 * fc * (b * h - As / 10000) + fy * As / 10000) * 1000; // kN
    points.push({ Pn: PnMax, Mn: 0 });
    
    // 2. Punto de falla balanceada
    const cb = (ecu / (ecu + ey)) * d;
    const ab = beta1 * cb;
    
    // Fuerzas en el hormigón
    const Cc = 0.85 * fc * ab * b * 1000; // kN
    
    // Deformaciones y esfuerzos en el acero
    const es1 = ecu * (cb - dPrime) / cb;
    const fs1 = Math.min(fy, es1 * 200000);
    const es2 = ecu * (d - cb) / cb;
    const fs2 = Math.min(fy, es2 * 200000);
    
    // Fuerzas en el acero
    const Cs = (fs1 * As / 2 / 10000) * 1000; // kN
    const Ts = (fs2 * As / 2 / 10000) * 1000; // kN
    
    // Carga y momento balanceados
    const Pnb = Cc + Cs - Ts;
    const Mnb = (Cc * (h/2 - ab/2) + Cs * (h/2 - dPrime) + Ts * (d - h/2)) * 1000; // kN-m
    points.push({ Pn: Pnb, Mn: Mnb });
    
    // 3. Punto de flexión pura
    // (Iteración para encontrar c que produzca Pn = 0)
    let cPure = 0;
    let tolerance = 1;
    let maxIter = 100;
    let iter = 0;
    
    while (tolerance > 0.1 && iter < maxIter) {
      iter++;
      const aPure = beta1 * cPure;
      
      // Deformaciones y esfuerzos
      const es1Pure = ecu * (cPure - dPrime) / cPure;
      const fs1Pure = Math.min(fy, es1Pure * 200000);
      const es2Pure = ecu * (d - cPure) / cPure;
      const fs2Pure = Math.min(fy, es2Pure * 200000);
      
      // Fuerzas
      const CcPure = 0.85 * fc * aPure * b * 1000;
      const CsPure = (fs1Pure * As / 2 / 10000) * 1000;
      const TsPure = (fs2Pure * As / 2 / 10000) * 1000;
      
      const PnPure = CcPure + CsPure - TsPure;
      
      tolerance = Math.abs(PnPure);
      cPure += PnPure / 10000; // Ajuste de c
    }
    
    const aPure = beta1 * cPure;
    const es1Pure = ecu * (cPure - dPrime) / cPure;
    const fs1Pure = Math.min(fy, es1Pure * 200000);
    const es2Pure = ecu * (d - cPure) / cPure;
    const fs2Pure = Math.min(fy, es2Pure * 200000);
    
    const CcPure = 0.85 * fc * aPure * b * 1000;
    const CsPure = (fs1Pure * As / 2 / 10000) * 1000;
    const TsPure = (fs2Pure * As / 2 / 10000) * 1000;
    
    const MnPure = (CcPure * (h/2 - aPure/2) + CsPure * (h/2 - dPrime) + TsPure * (d - h/2)) * 1000;
    points.push({ Pn: 0, Mn: MnPure });
    
    // Generar más puntos para la curva
    for (let c = 0.1; c < h * 3; c += 0.05) {
      const a = beta1 * c;
      
      // Deformaciones y esfuerzos
      const es1 = ecu * (c - dPrime) / c;
      const fs1 = Math.min(fy, es1 * 200000);
      const es2 = ecu * (d - c) / c;
      const fs2 = Math.min(fy, es2 * 200000);
      
      // Fuerzas
      const Cc = 0.85 * fc * a * b * 1000;
      const Cs = (fs1 * As / 2 / 10000) * 1000;
      const Ts = (fs2 * As / 2 / 10000) * 1000;
      
      const Pn = Cc + Cs - Ts;
      const Mn = (Cc * (h/2 - a/2) + Cs * (h/2 - dPrime) + Ts * (d - h/2)) * 1000;
      
      points.push({ Pn, Mn });
    }
    
    setDiagramPoints(points);
    
    // Escalar puntos para el gráfico
    const maxPn = Math.max(...points.map(p => p.Pn)) * 1.1;
    const maxMn = Math.max(...points.map(p => p.Mn)) * 1.1;
    
    const scaleX = 700 / maxMn;
    const scaleY = 400 / maxPn;
    
    // Dibujar ejes
    draw.line(50, 450, 750, 450).stroke({ color: '#000', width: 2 }); // Eje X
    draw.line(50, 450, 50, 50).stroke({ color: '#000', width: 2 });   // Eje Y
    
    // Etiquetas de ejes
    draw.text('Momento (kN-m)').move(350, 470).font({ size: 12 });
    draw.text('Carga Axial (kN)').move(10, 250).rotate(-90).font({ size: 12 });
    
    // Dibujar diagrama de interacción
    const pathData = points.map((point, i) => {
      const x = 50 + point.Mn * scaleX;
      const y = 450 - point.Pn * scaleY;
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    }).join(' ');
    
    draw.path(pathData)
      .fill('none')
      .stroke({ color: '#3182ce', width: 3 });
    
    // Dibujar punto de diseño
    const designX = 50 + Mu * scaleX;
    const designY = 450 - Pu * scaleY;
    
    draw.circle(8).fill('#e53e3e').move(designX - 4, designY - 4);
    draw.text(`(Mu=${Mu}, Pu=${Pu})`).move(designX + 10, designY - 10).font({ size: 12, fill: '#e53e3e' });
    
    // Dibujar puntos clave
    const balancePoint = points.find(p => Math.abs(p.Pn - Pnb) < 0.1 * Pnb);
    if (balancePoint) {
      const bx = 50 + balancePoint.Mn * scaleX;
      const by = 450 - balancePoint.Pn * scaleY;
      draw.circle(6).fill('#38a169').move(bx - 3, by - 3);
      draw.text('Balanceado').move(bx + 10, by - 10).font({ size: 10, fill: '#38a169' });
    }
    
    // Dibujar compresión pura
    draw.circle(6).fill('#805ad5').move(50 - 3, 450 - PnMax * scaleY - 3);
    draw.text('Compresión pura').move(60, 450 - PnMax * scaleY - 10).font({ size: 10, fill: '#805ad5' });
    
    // Dibujar flexión pura
    const flexPoint = points.find(p => Math.abs(p.Pn) < 0.1 * PnMax);
    if (flexPoint) {
      const fx = 50 + flexPoint.Mn * scaleX;
      const fy = 450 - flexPoint.Pn * scaleY;
      draw.circle(6).fill('#d69e2e').move(fx - 3, fy - 3);
      draw.text('Flexión pura').move(fx + 10, fy - 10).font({ size: 10, fill: '#d69e2e' });
    }
    
    // Verificar si el punto está dentro del diagrama
    const isInside = checkPointInDiagram(Pu, Mu, points);
    draw.text(`Estado: ${isInside ? 'Seguro' : 'Inseguro'}`)
      .move(600, 30)
      .font({ size: 14, fill: isInside ? '#38a169' : '#e53e3e', weight: 'bold' });
  }, [b, h, recubrimiento, fc, fy, numBarras, diametroBarras, Pu, Mu]);

  // Función para verificar si un punto está dentro del diagrama
  const checkPointInDiagram = (Pu, Mu, points) => {
    // Ordenar puntos por momento
    const sortedPoints = [...points].sort((a, b) => a.Mn - b.Mn);
    
    // Encontrar los puntos adyacentes
    let lower = null;
    let upper = null;
    
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      if (sortedPoints[i].Mn <= Mu && sortedPoints[i+1].Mn >= Mu) {
        lower = sortedPoints[i];
        upper = sortedPoints[i+1];
        break;
      }
    }
    
    if (!lower || !upper) return false;
    
    // Interpolar para encontrar la Pn correspondiente a Mu
    const t = (Mu - lower.Mn) / (upper.Mn - lower.Mn);
    const interpolatedPn = lower.Pn + t * (upper.Pn - lower.Pn);
    
    return Pu <= interpolatedPn;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Vista de la columna */}
      <div className="w-2/5 p-4">
        <div className="bg-white shadow rounded-lg p-4 h-full">
          <h2 className="text-xl font-bold mb-4">Vista de la Columna</h2>
          <div 
            ref={canvasRef} 
            className="border border-gray-300 rounded-lg"
            style={{ height: '500px' }}
          ></div>
          
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Ancho (b) [m]</label>
              <input
                type="number"
                step="0.05"
                min="0.2"
                max="1.0"
                value={b}
                onChange={(e) => setB(Number(e.target.value))}
                className="w-full border rounded p-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Altura (h) [m]</label>
              <input
                type="number"
                step="0.05"
                min="0.2"
                max="1.0"
                value={h}
                onChange={(e) => setH(Number(e.target.value))}
                className="w-full border rounded p-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Recubrimiento [m]</label>
              <input
                type="number"
                step="0.01"
                min="0.03"
                max="0.1"
                value={recubrimiento}
                onChange={(e) => setRecubrimiento(Number(e.target.value))}
                className="w-full border rounded p-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">N° Barras</label>
              <select
                value={numBarras}
                onChange={(e) => setNumBarras(Number(e.target.value))}
                className="w-full border rounded p-2"
              >
                {[4, 6, 8, 10, 12].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Diámetro Barras [mm]</label>
              <select
                value={diametroBarras}
                onChange={(e) => setDiametroBarras(Number(e.target.value))}
                className="w-full border rounded p-2"
              >
                {barOptions.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">f'c [MPa]</label>
              <input
                type="number"
                step="1"
                min="15"
                max="50"
                value={fc}
                onChange={(e) => setFc(Number(e.target.value))}
                className="w-full border rounded p-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">fy [MPa]</label>
              <input
                type="number"
                step="50"
                min="300"
                max="600"
                value={fy}
                onChange={(e) => setFy(Number(e.target.value))}
                className="w-full border rounded p-2"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Diagrama de interacción y cargas */}
      <div className="w-3/5 p-4">
        <div className="bg-white shadow rounded-lg p-4 h-full">
          <h2 className="text-xl font-bold mb-4">Diagrama de Interacción</h2>
          <div 
            ref={interactionDiagramRef}
            className="border border-gray-300 rounded-lg mb-4"
            style={{ height: '500px' }}
          ></div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Cargas de Diseño</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Carga Axial (Pu) [kN]</label>
                <input
                  type="number"
                  value={Pu}
                  onChange={(e) => setPu(Number(e.target.value))}
                  className="w-full border rounded p-2"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Momento (Mu) [kN-m]</label>
                <input
                  type="number"
                  value={Mu}
                  onChange={(e) => setMu(Number(e.target.value))}
                  className="w-full border rounded p-2"
                />
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-2">Resultados</h3>
              
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-700">Área de acero: </span>
                <span className="font-semibold">
                  {(Math.PI * Math.pow(diametroBarras / 2 / 10, 2) * numBarras).toFixed(2)} cm²
                </span>
              </div>
              
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-700">Cuantía: </span>
                <span className="font-semibold">
                  {((Math.PI * Math.pow(diametroBarras / 2 / 10, 2) * numBarras) / (b * h * 10000) * 100).toFixed(2)} %
                </span>
              </div>
              
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-700">Carga balanceada: </span>
                <span className="font-semibold">
                  {diagramPoints.length > 0 ? 
                    (diagramPoints.find(p => p.Mn > 0 && p.Pn > 0)?.Pn?.toFixed(0) || 'N/A') : 'N/A'} kN
                </span>
              </div>
              
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-700">Momento balanceado: </span>
                <span className="font-semibold">
                  {diagramPoints.length > 0 ? 
                    (diagramPoints.find(p => p.Mn > 0 && p.Pn > 0)?.Mn?.toFixed(0) || 'N/A') : 'N/A'} kN-m
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Columnas;